import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, User, ArrowRight, AlertCircle } from 'lucide-react';
import BusyBeeLogo from '../components/BusyBeeLogo';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import SocialLoginButtons from '../components/auth/SocialLoginButtons';
import DesktopSignIn from '../desktop/DesktopSignIn';
import { isDesktop } from '../native/desktop';
import { MIN_PASSWORD_LENGTH } from '../lib/constants';
import { detailToMessage } from '../lib/apiError';
import { isValidEmail, meetsPasswordComplexity } from '../lib/validation';
import { PasswordInput } from '../components/ui/PasswordInput';
import { PasswordStrengthMeter } from '../components/auth/PasswordStrengthMeter';
import { rememberSignInReturn, takeSignInReturn } from '../lib/signInReturn';

// Codes the server answers when its quiet checks refuse a sign-up (utils/signup_guard.py).
const SIGNUP_REFUSALS = ['signup_blocked', 'disposable_email', 'too_many_signups'];

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  // Quiet bot checks (backend utils/signup_guard.py): when the form appeared, and a field only bots fill.
  const openedAt = useRef(Date.now());
  const [website, setWebsite] = useState('');
  const prefillEmail = searchParams.get('email') || '';
  const redirect = searchParams.get('redirect');
  useEffect(() => { rememberSignInReturn(redirect); }, [redirect]);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // `required` on the inputs passes for whitespace-only values, so the
    // emptiness checks have to run on the trimmed strings.
    const trimmedName = fullName.trim();
    if (!trimmedName) {
      setError(t('auth.full_name_required'));
      return;
    }

    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      setError(t('auth.invalid_email'));
      return;
    }

    if (!password.trim()) {
      setError(t('auth.password_whitespace_only'));
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('auth.password_min_length', { min: MIN_PASSWORD_LENGTH }));
      return;
    }

    // Enforce the complexity policy (mirrors the backend) so weak passwords
    // like "abcdef" are rejected with a clear message before submitting.
    if (!meetsPasswordComplexity(password)) {
      setError(t('auth.password_complexity'));
      return;
    }

    // BG_63: confirm field lets the user verify they typed the password they
    // intended before an account is created with it.
    if (password !== confirmPassword) {
      setError(t('auth.passwords_do_not_match'));
      return;
    }

    setLoading(true);

    try {
      const source = {
        language: (i18n.resolvedLanguage || i18n.language || '').split('-')[0] || undefined,
        referrer: document.referrer || undefined,
        utm_source: searchParams.get('utm_source') || undefined,
        utm_medium: searchParams.get('utm_medium') || undefined,
        utm_campaign: searchParams.get('utm_campaign') || undefined,
      };
      await register(trimmedEmail, password, trimmedName, {
        website: website || undefined, form_ms: Date.now() - openedAt.current, source,
      });
      navigate(takeSignInReturn(redirect), { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(SIGNUP_REFUSALS.includes(detail) ? t(`auth.${detail}`)
        : detailToMessage(detail) || t('auth.register_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex">
      <SEO title="Sign Up Free" description="Create your free BusyBee.Day account. AI-powered task management, projects, habits, focus time, documents, and more." canonical="/register" />
      {/* Left side - Form. flex-col + my-auto (not items-center) so a form
          taller than the viewport flows from the top and the page scrolls,
          instead of being clipped by centered flex overflow. */}
      <div className="flex-1 flex flex-col items-center px-8 py-12">
        <div className="w-full max-w-md my-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <BusyBeeLogo className="h-10" />
          </Link>

          <h1 className="text-3xl font-bold text-white mb-2">{t('auth.create_account')}</h1>
          {/* Signing up gives the Free plan; the 14-day trial comes only with a paid plan's checkout. */}
          <p className="text-gray-400 mb-8">{t('landing:cta.trust_free')} · {t('landing:cta.trust_no_card')}</p>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {isDesktop() ? <DesktopSignIn /> : <SocialLoginButtons mode="register" />}

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-800" />
            <span className="text-xs uppercase tracking-widest text-gray-500">{t('auth.or')}</span>
            <div className="flex-1 h-px bg-gray-800" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* People never see or reach this field; bots that fill every field give themselves away. */}
            <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
              <label htmlFor="website">Website</label>
              <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off"
                value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="fullName" className="text-gray-300">{t('auth.full_name_label')}</Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="pl-10 bg-white/5 border-gray-800 text-white placeholder:text-gray-500"
                  placeholder={t('auth.full_name_placeholder')}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-300">{t('auth.email_label')}</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-white/5 border-gray-800 text-white placeholder:text-gray-500"
                  placeholder={t('auth.email_placeholder')}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-300">{t('auth.password_label')}</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-white/5 border-gray-800 text-white placeholder:text-gray-500"
                  placeholder={t('auth.password_placeholder')}
                  required
                />
              </div>
              {/* The whole rule up front, not only after a refused submit. */}
              <p className="mt-1 text-xs text-gray-500">{t('auth.password_min_length_hint', { min: MIN_PASSWORD_LENGTH })}</p>
              <p className="text-xs text-gray-500">{t('auth.password_complexity')}</p>
              {/* BG_62: live strength hint so users are encouraged toward a
                  secure password instead of only seeing the minimum rule. */}
              <PasswordStrengthMeter password={password} />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-gray-300">{t('auth.confirm_password_label')}</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <PasswordInput
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-white/5 border-gray-800 text-white placeholder:text-gray-500"
                  placeholder={t('auth.confirm_password_placeholder')}
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-amber-500 text-black hover:bg-amber-400 h-12"
              disabled={loading}
            >
              {loading ? t('auth.sign_up_action') : t('auth.create_account_action')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <p className="mt-8 text-center text-gray-400">
            {t('auth.already_have_account')}{' '}
            <Link
              to={`/login${searchParams.toString() ? `?${searchParams.toString()}` : ''}`}
              className="text-amber-400 hover:text-amber-300"
            >
              {t('auth.sign_in')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-amber-900/30 to-orange-900/30 border-l border-gray-800">
        <div className="text-center px-12">
          {/* Was "Join 50,000+ professionals / reclaimed 8+ hours per week".
              Neither number was ever measured, so this now leads with what the
              product does rather than with an invented adoption claim. */}
          <h2 className="text-4xl font-bold text-white mb-4">{t('auth.hero_title')}</h2>
          <p className="text-xl text-gray-400">{t('auth.hero_subtitle')}</p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
